import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TEMPLATES, KIDS_TEMPLATES } from "@/lib/templates";
import { outlineToMindmap } from "@/lib/mindmapConvert";
import { CARD, ICON_BTN } from "@/lib/styles";
import { X, FileText, Network, Sparkles, Loader2, FilePlus, Wallet, Baby, Sigma, Scale } from "lucide-react";
import { toast } from "sonner";

// type: "text" | "mindmap"; folderId: carpeta destino ("" = ninguna)
export function TemplatePicker({ open, type, folderId = "", onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [kidsIdeas, setKidsIdeas] = useState(false);
  if (!open) return null;

  const isMap = type === "mindmap";

  const createBoard = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/notes", {
        type: "board",
        title: "Pizarra",
        board: { displayCurrency: "USD", rate: 0, blocks: [] },
        folder_id: folderId || null,
      });
      onClose?.();
      navigate(`/pizarra/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createMoveMap = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/notes", {
        type: "movemap",
        title: "Mapa de flujo económico",
        movemap: { displayCurrency: "USD", rate: 0, movements: [] },
        folder_id: folderId || null,
      });
      onClose?.();
      navigate(`/movimientos/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createLedger = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/notes", {
        type: "ledger",
        title: "Mapa Contable",
        ledger: { displayCurrency: "USD", rate: 0, accounts: [], entries: [] },
        folder_id: folderId || null,
      });
      onClose?.();
      navigate(`/contable/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const create = async (tpl) => {
    if (busy) return;
    setBusy(true);
    try {
      let payload;
      if (isMap) {
        const title = tpl ? tpl.name : "Nuevo mapa mental";
        const mindmap = tpl ? outlineToMindmap(title, tpl.outline, !!tpl.kids) : { nodes: [], edges: [] };
        if (tpl?.kids) mindmap.kids = true;
        payload = { type: "mindmap", title, mindmap, folder_id: folderId || null };
      } else {
        const title = tpl ? tpl.name : "Nueva nota";
        const content = tpl ? `# ${tpl.name}\n\n${tpl.outline}` : "";
        payload = { type: "text", title, content, folder_id: folderId || null };
      }
      const { data } = await api.post("/notes", payload);
      onClose?.();
      navigate(isMap ? `/mindmap/${data.id}` : `/note/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose?.()} data-testid="template-picker">
      <div className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-xl flex items-center gap-2">
            {isMap ? <Network size={20} className="text-indigo-500" /> : <FileText size={20} className="text-sky-500" />}
            {isMap ? "Nuevo mapa mental" : "Nueva nota"}
          </h3>
          <button onClick={() => onClose?.()} className={ICON_BTN} data-testid="template-close"><X size={16} /></button>
        </div>
        <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-5 flex items-center gap-1.5">
          <Sparkles size={14} className="text-amber-500" /> Empieza en blanco o elige una plantilla para guiarte.
        </p>

        {busy && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 rounded-2xl">
            <Loader2 className="animate-spin text-indigo-500" size={26} />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <button onClick={() => create(null)} className={`${CARD} p-4 text-left flex items-start gap-3 border-dashed`} data-testid="template-blank">
            <div className="w-10 h-10 rounded-lg bg-slate-500/10 text-slate-500 flex items-center justify-center shrink-0"><FilePlus size={20} /></div>
            <div>
              <p className="font-heading font-medium text-sm">En blanco</p>
              <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Empieza desde cero.</p>
            </div>
          </button>
          {isMap && (
            <button onClick={createBoard} className={`${CARD} p-4 text-left flex items-start gap-3 border-indigo-300 dark:border-indigo-500/40 bg-gradient-to-br from-indigo-50 to-transparent dark:from-indigo-500/5`} data-testid="template-board">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-500 flex items-center justify-center shrink-0"><Sigma size={20} /></div>
              <div>
                <p className="font-heading font-medium text-sm flex items-center gap-1.5">Pizarra <span className="text-[9px] uppercase tracking-wide bg-indigo-500 text-white px-1.5 py-0.5 rounded">Lienzo</span></p>
                <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Crea varios mapas de flujo (Casa, Finca, Negocio…) en un mismo lienzo y ve el Total General automático.</p>
              </div>
            </button>
          )}
          {isMap && (
            <button onClick={createMoveMap} className={`${CARD} p-4 text-left flex items-start gap-3 border-orange-300 dark:border-orange-500/40 bg-gradient-to-br from-orange-50 to-transparent dark:from-orange-500/5`} data-testid="template-movemap">
              <div className="w-10 h-10 rounded-lg bg-orange-500/15 text-orange-500 flex items-center justify-center shrink-0"><Wallet size={20} /></div>
              <div>
                <p className="font-heading font-medium text-sm flex items-center gap-1.5">Mapa de flujo económico <span className="text-[9px] uppercase tracking-wide bg-orange-500 text-white px-1.5 py-0.5 rounded">Inteligente</span></p>
                <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Ingresos, egresos y haber automáticos. Los meses se crean solos.</p>
              </div>
            </button>
          )}
          {isMap && (
            <button onClick={createLedger} className={`${CARD} p-4 text-left flex items-start gap-3 border-emerald-300 dark:border-emerald-500/40 bg-gradient-to-br from-emerald-50 to-transparent dark:from-emerald-500/5`} data-testid="template-ledger">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0"><Scale size={20} /></div>
              <div>
                <p className="font-heading font-medium text-sm flex items-center gap-1.5">Mapa Contable <span className="text-[9px] uppercase tracking-wide bg-emerald-500 text-white px-1.5 py-0.5 rounded">Cuentas</span></p>
                <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Cuentas flotantes (Banco, Casa, Deudas…) con saldos. Mueve dinero entre ellas con cables y ve el Balance General.</p>
              </div>
            </button>
          )}
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => create(t)} className={`${CARD} p-4 text-left flex items-start gap-3`} data-testid={`template-${t.id}`}>
                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0"><Icon size={20} /></div>
                <div>
                  <p className="font-heading font-medium text-sm">{t.name}</p>
                  <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {isMap && (
          <div className="mt-4" data-testid="kids-templates">
            <p className="font-heading font-medium text-sm flex items-center gap-1.5 mb-2 text-pink-500"><Baby size={16} /> Para niños <span className="text-[9px] uppercase tracking-wide bg-pink-500 text-white px-1.5 py-0.5 rounded">Modo Niños</span></p>
            <button onClick={() => create({ name: "Mi mapa", outline: "", kids: true })} className={`${CARD} w-full p-4 text-left flex items-start gap-3 border-pink-300 dark:border-pink-500/40 bg-gradient-to-br from-pink-50 to-transparent dark:from-pink-500/5`} data-testid="template-kids-blank">
              <div className="w-10 h-10 rounded-lg bg-pink-500/15 text-pink-500 flex items-center justify-center shrink-0"><Baby size={20} /></div>
              <div>
                <p className="font-heading font-medium text-sm">Empezar en blanco (el niño crea)</p>
                <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Lienzo vacío en Modo Niños: añade ramas, elige emojis y formas a tu gusto.</p>
              </div>
            </button>

            <button onClick={() => setKidsIdeas((v) => !v)} className="mt-2 text-xs text-pink-500 hover:underline flex items-center gap-1" data-testid="toggle-kids-ideas">
              {kidsIdeas ? "Ocultar" : "Ver"} ideas de plantillas (editables) →
            </button>
            {kidsIdeas && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[32vh] overflow-y-auto pr-1 mt-2">
                {KIDS_TEMPLATES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button key={t.id} onClick={() => create(t)} className={`${CARD} p-4 text-left flex items-start gap-3 border-pink-200 dark:border-pink-500/30`} data-testid={`template-${t.id}`}>
                      <div className="w-10 h-10 rounded-lg bg-pink-500/10 text-pink-500 flex items-center justify-center shrink-0"><Icon size={20} /></div>
                      <div>
                        <p className="font-heading font-medium text-sm">{t.name}</p>
                        <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
